import React, { ForwardRefRenderFunction, forwardRef } from "react";
//#region redux
import { useSelector } from "react-redux";
import { AppState } from "store";
//#endregion
//#region types
import { FormHandles } from "models/propTypes/genericFormProps";
import { ArticleFormTypes } from "models/formTypes";
import { TranslationKeys } from "translations";
import { ArticleCreateFormContainerProps } from "models/propTypes/articlePropTypes";
import { AutocompleteListItem } from "models/propTypes/listPropTypes";
import { InvoiceConstants } from "utils/constants";
import { SelectionItem } from "models/utilTypes/selectionTypes";
import { WintSuperkollAPIModel, WintSuperkollAPIModelAccounting } from "wint-api-client/dist/types";
//#endregion
//#region components
import ArticleCreateForm from "./createForm";
import { useInvoiceService, useArticleService, useSearchService, useAccountService } from "serviceHooks";
//#endregion
import * as Yup from "yup";
import { UtilsHelper } from "utils/helpers";

const ArticleCreateFormContainer: ForwardRefRenderFunction<FormHandles, ArticleCreateFormContainerProps> = (
  props,
  ref
) => {
  const invoiceService = useInvoiceService();
  const searchService = useSearchService();
  const articleService = useArticleService();
  const accountService = useAccountService();
  const noVatCompany = useSelector((store: AppState) => store.auth.selectedCompany.noVat);

  const [units, setUnits] = React.useState<AutocompleteListItem[]>([]);
  const [vatTypes, setVatTypes] = React.useState<SelectionItem[]>([]);
  const [revenueAccounts, setRevenueAccounts] =
    React.useState<WintSuperkollAPIModelAccounting.RevenueAccountsResponse>();

  React.useEffect(() => {
    invoiceService.getUnits().then((res) => {
      if (res && res.length > 0) {
        const newUnits = res.map(
          (item): AutocompleteListItem => ({
            id: item.id,
            value: item.name ?? "",
          })
        );
        setUnits(newUnits.reverse());
      } else if (units.length !== 0) setUnits([]);
    });

    searchService.vatTypes("").then((res) => setVatTypes(res.map((type) => ({ id: type.id ?? 0, value: type.value }))));

    accountService
      .getRevenueAccounts()
      .then(setRevenueAccounts)
      .catch(() => setRevenueAccounts({ enabled: false }));
  }, []);

  const validationSchema = Yup.object<ArticleFormTypes.Article>({
    name: Yup.string().required(TranslationKeys.Validations.Required),
    unitPrice: Yup.number().required(TranslationKeys.Validations.Required),
    vat: Yup.number(),
    unit: Yup.string(),
    unitId: Yup.number(),
    euVatCategoryId: Yup.number(),
  });

  const defaultValues: ArticleFormTypes.Article = {
    vat: noVatCompany ? 0 : InvoiceConstants.DefaultVatType,
    unit: UtilsHelper.getSelectionNoneText(),
    euVatCategoryId: WintSuperkollAPIModel.EuVatProductType.NotDefined,
    euVatCategory: invoiceService.getEuVatCategoryText(WintSuperkollAPIModel.EuVatProductType.NotDefined),
  };

  const handleSubmit = async (values: ArticleFormTypes.Article) => {
    articleService.create({
      text: values.name,
      unitPrice: values.unitPrice ?? 0,
      unitId: values.unitId,
      vat: values.vat ?? 0,
      euVatProductType: values.euVatCategoryId,
      inactive: false,
      // account is tri-state server-side (omit/undefined = leave unchanged, 0 = explicitly clear, a
      // number = set it), but this form always submits its full current state, so an empty selection
      // must be sent as an explicit 0 rather than omitted.
      account: values.account ?? 0,
    });
  };

  return (
    <ArticleCreateForm
      ref={ref}
      {...props}
      validationSchema={validationSchema}
      defaultValues={defaultValues}
      handleSubmit={handleSubmit}
      units={units}
      vatTypes={vatTypes}
      revenueAccounts={revenueAccounts}
      noVatCompany={noVatCompany}
    />
  );
};

export default forwardRef(ArticleCreateFormContainer);
